import React, { useEffect, useImperativeHandle, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import axios from "axios";
import { isAuth } from "../../../../pages/auth/ProtectedRoute";
import styles from "../../../../styles/mailCenter/createTemplateModal.module.scss";
import VisualEditor from "./VisualEditor";


const RichToolbar: React.FC<{ editor: ReturnType<typeof useEditor> | null }> = ({ editor }) => {
  if (!editor) return null;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLink = () => {
    const url = window.prompt("Enter URL");
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  const handleImage = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("files[0]", file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_USER_API}uploads/file`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${isAuth()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        const filepath = response.data.data[0].filepath;
        const fullUrl = `${process.env.REACT_APP_IMAGE_API_PREFIX}${filepath}`;
        editor.chain().focus().setImage({ src: fullUrl }).run();
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={styles["rich-toolbar"]}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        style={{ display: "none" }}
      />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`${styles["rich-toolbar__btn"]} ${editor.isActive("bold") ? styles["rich-toolbar__btn--active"] : ""}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
          <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`${styles["rich-toolbar__btn"]} ${editor.isActive("italic") ? styles["rich-toolbar__btn--active"] : ""}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="19" y1="4" x2="10" y2="4" />
          <line x1="14" y1="20" x2="5" y2="20" />
          <line x1="15" y1="4" x2="9" y2="20" />
        </svg>
      </button>
      <div className={styles["rich-toolbar__divider"]} />
      <button type="button" onClick={handleLink} className={styles["rich-toolbar__btn"]}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      </button>
      <button type="button" onClick={handleImage} className={styles["rich-toolbar__btn"]}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <polyline points="21 15 16 10 5 21" />
        </svg>
      </button>
      <div className={styles["rich-toolbar__divider"]} />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`${styles["rich-toolbar__btn"]} ${editor.isActive("bulletList") ? styles["rich-toolbar__btn--active"] : ""}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
      </button>
      <span className={styles["rich-toolbar__hint"]}>Use HTML tags for formatting</span>
    </div>
  );
};

const RichTextEditor = React.forwardRef<RichTextEditorRef, RichTextEditorProps>(
  (
    {
      bodyMode,
      onBodyModeChange,
      plainTextValue,
      onPlainTextChange,
      richTextValue,
      onRichTextChange,
      placeholder = "Write your email content here. Use variables like {{first_name}} for personalization.",
      htmlTemplate,
    },
    ref
  ) => {
    const wrapOut = (html: string) => {
      if (!htmlTemplate) return html;
      if (!htmlTemplate.includes("{{content}}")) return html;
      return htmlTemplate.replace("{{content}}", html);
    };

    const unwrapIn = (html: string) => {
      if (!htmlTemplate) return html;
      if (!htmlTemplate.includes("{{content}}")) return html;
      const [prefix, suffix] = htmlTemplate.split("{{content}}");
      if (html.startsWith(prefix) && html.endsWith(suffix)) {
        return html.slice(prefix.length, html.length - suffix.length);
      }
      return html;
    };
    const editor = useEditor({
      extensions: [StarterKit, Link.configure({ openOnClick: false }), Image],
      content: unwrapIn(richTextValue || ""),
      editorProps: {
        attributes: {
          "data-placeholder": placeholder,
        },
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const wrapped = wrapOut(html);
        if (wrapped !== richTextValue) {
          onRichTextChange(wrapped);
        }
      },
    });
    const visualEditorRef = useRef<any>(null);

    useImperativeHandle(ref, () => ({
      insertVariable: (variable: string) => {
        if (bodyMode === "Plain Text") {
          onPlainTextChange(plainTextValue + variable);
          return;
        }
        if (bodyMode === "Rich Text" && editor) {
          editor.chain().focus().insertContent(variable).run();
          // Schedule the HTML update to ensure the content is properly inserted
          setTimeout(() => {
            onRichTextChange(wrapOut(editor.getHTML()));
          }, 0);
          return;
        }

        if (bodyMode === "Visual Editor" && visualEditorRef.current) {
          try {
            visualEditorRef.current.insertVariable(variable);
          } catch (err) {
            console.error("VisualEditor insertVariable error", err);
          }
          return;
        }
      },
      reset: () => {
        if (editor) {
          editor.commands.clearContent(true);
        }
        if (visualEditorRef.current) {
          try {
            visualEditorRef.current.reset();
          } catch (err) {
            console.error("VisualEditor reset error", err);
          }
        }
      },
      exportHtml: async () => {
        try {
          if (bodyMode === "Plain Text") {
            return plainTextValue || "";
          }

          if (bodyMode === "Rich Text" && editor) {
            return wrapOut(editor.getHTML());
          }

          if (bodyMode === "Visual Editor" && visualEditorRef.current) {
            try {
              const data = await visualEditorRef.current.exportHtml();
              return data?.html || "";
            } catch (err) {
              console.error("VisualEditor exportHtml error", err);
            }
          }

          // fallback
          return richTextValue || "";
        } catch (err) {
          console.error("exportHtml error", err);
          return richTextValue || "";
        }
      },
    }), [bodyMode, editor, onPlainTextChange, onRichTextChange, plainTextValue]);

    useEffect(() => {
      if (!editor) return;
      if (bodyMode !== "Rich Text") return;

      if (richTextValue && unwrapIn(richTextValue) !== editor.getHTML()) {
        editor.commands.setContent(unwrapIn(richTextValue));
      } else if (!richTextValue && plainTextValue && editor.isEmpty) {
        editor.commands.setContent(plainTextValue);
      }
    }, [bodyMode, editor, plainTextValue, richTextValue]);

    const isRichTextEmpty = !editor || editor.isEmpty || editor.getHTML() === "<p></p>";

    return (
      <>
        <div className={styles["body-header__actions"]}>
          {(["Plain Text", "Rich Text", "Visual Editor"] as BodyMode[]).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onBodyModeChange(mode)}
              className={[
                styles["body-header__mode-btn"],
                bodyMode === mode ? styles["body-header__mode-btn--active"] : "",
              ].join(" ")}
            >
              {mode}
            </button>
          ))}
        </div>
        {bodyMode === "Plain Text" ? (
          <textarea
            className={styles["field__textarea"]}
            placeholder={placeholder}
            value={plainTextValue}
            onChange={(e) => onPlainTextChange(e.target.value)}
            rows={8}
          />
        ) : bodyMode === "Rich Text" ? (
          <>
            <RichToolbar editor={editor} />
            <div className={styles["rich-editor"]}>
              <EditorContent editor={editor} />
            </div>
          </>
        ) : (
          <VisualEditor ref={visualEditorRef} html={richTextValue} onHtmlChange={onRichTextChange} />
        )}
      </>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";

export default RichTextEditor;
